import React from 'react';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { blur, Field, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { inclusion, numericality, required } from 'redux-form-validators';
import { connect } from 'react-redux';
import { DefaultButton, ReduxSelectField, ReduxTextField, SliderField } from 'common/components';
import {
  allocationMethodByEquity,
  allocationMethodFixed,
  allocationMethods,
  allocationPercentMax,
  allocationPercentMin,
  allocationPercentStep,
} from '../../constants';
import { editInvestment, getInvestStatement } from '../../_redux';
import { percentToRatio } from '../../helpers';
import { staticStyles } from './style';

const InvestEditRatioForm = ({
  investmentId,
  toggleModal,
  handleSubmit,
  dispatch,
  submitting,
  invalid,
  intl,
  error,
  getInvestStatement,
}) => {
  const submit = handleSubmit(values =>
    editInvestment(
      {
        investmentId,
        ...values,
        ratio: percentToRatio(values.ratio),
        onEditSuccess: () => getInvestStatement({ investmentId }),
      },
      dispatch
    )
  );

  const allocationMethodOptions = [
    { label: intl.formatMessage({ id: 'justByEquity' }), value: allocationMethodByEquity },
    { label: intl.formatMessage({ id: 'justFixed' }), value: allocationMethodFixed },
  ];

  const handleRatioBlur = (event, value) => {
    if (value === '') {
      return;
    }

    const normalizedValue = Math.min(allocationPercentMax, Math.max(allocationPercentMin, Number(value)));

    if (normalizedValue !== Number(value)) {
      event.preventDefault();
      dispatch(blur('InvestEditRatioForm', 'ratio', normalizedValue));
    }
  };

  return (
    <form className="BasicModalForm" onSubmit={submit}>
      <Field
        component={ReduxTextField}
        textId="justPercent"
        type="number"
        name="ratio"
        validate={[
          required(),
          numericality({ '>=': allocationPercentMin, '<=': allocationPercentMax, allowBlank: true }),
        ]}
        disabled={submitting}
        description="%"
        onBlur={handleRatioBlur}
        shouldUseOnlyFormValue
      />
      <Field
        component={SliderField}
        name="ratio"
        minValue={allocationPercentMin}
        maxValue={allocationPercentMax}
        step={allocationPercentStep}
        unit="%"
        disabled={submitting}
      />
      <div className="InvestEditRatioForm__caption">
        <FormattedMessage id="investAllocationDescription" />
      </div>
      <Field
        component={ReduxSelectField}
        name="allocationMethod"
        textId="justAllocationMethod"
        options={allocationMethodOptions}
        disabled={submitting}
        validate={[required(), inclusion({ in: allocationMethods })]}
      />

      {error && <strong className="formError">{error}</strong>}

      <div className="BasicModalForm__buttons">
        <DefaultButton textId="justEdit" type="submit" loading={submitting} disabled={invalid || submitting} filled />
        <DefaultButton textId="justCancel" type="button" onClick={() => toggleModal()} />
      </div>
      <style jsx>{staticStyles}</style>
    </form>
  );
};

InvestEditRatioForm.propTypes = {
  investmentId: PropTypes.number.isRequired,
  toggleModal: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  error: PropTypes.string,
  getInvestStatement: PropTypes.func.isRequired,
};

InvestEditRatioForm.defaultProps = {
  error: '',
};

export default compose(
  injectIntl,
  connect(() => ({}), { getInvestStatement: data => getInvestStatement.request(data) }),
  reduxForm({
    form: 'InvestEditRatioForm',
    onSubmitSuccess: (result, dispatch, props) => {
      props.toggleModal();
    },
  })
)(InvestEditRatioForm);
export { InvestEditRatioForm };
